import {
  Button,
  Card,
  Expander,
  ExpanderContent,
  ExpanderHeader,
  ResponsiveCheckboxTable,
  ResponsiveTableRow,
  Separator,
  H5,
  colors,
} from '@fs/zion-ui'
import React, { useCallback, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ContactChip } from '@fs/zion-contact-overlay'
import { useFeatureFlags } from '@fs/zion-flags'
import { saveSLSActions, UNDO } from '@fs/zion-sls'
import { useR2StateContext } from '../../../providers/r2Provider/R2Context'
import { R2AppName, headers } from '../../../../shared/services/config'
import { errorCollector } from '../../../../shared/utils/utils'
import ErrorOverlay from '../../../../shared/components/errorOverlay/ErrorOverlay'

const getTime = (timestamp, i18n) => {
  const date = new Date(timestamp)
  return date.toLocaleString(i18n.language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

const getUser = (userId) => {
  if (userId?.startsWith('fs:')) userId = 'cis.wkca.MMMM-M932'
  return <ContactChip contact={{ cisId: userId }} />
}

export default function DeletedPersons({ deletedPersonActions, expanded }) {
  const { t, i18n } = useTranslation()
  const r2State = useR2StateContext()
  const { rip_sharedImageViewer_saveChanges: saveChangesFlag } = useFeatureFlags(['rip_sharedImageViewer_saveChanges'])
  const [saveError, setSaveError] = useState()
  const [selectedRows, setSelectedRows] = useState([])
  const [deletedPersonsExpanded, setDeletedPersonsExpanded] = useState(true)

  useEffect(() => {
    setDeletedPersonsExpanded(expanded)
  }, [expanded])

  /**
   * Save sls.  SlsReducer doesn't support UNDO so we will just reload the page.  It is too hard to implement UNDO to restore persons
   */
  const saveSls = useCallback(
    (slsActions) => {
      if (saveChangesFlag.isOn) {
        saveSLSActions({
          actions: slsActions,
          userId: r2State.userId,
          appName: R2AppName,
          fsUserAgentChain: headers['FS-User-Agent-Chain'],
        })
          .then(() => {
            window.location.assign(`${window.location.origin}${window.location.pathname}?view=index`)
          })
          .catch((err) => {
            setSaveError(err)
            errorCollector(err, null, {
              codeId: 73,
              slsActions,
              status: err?.response?.status,
              responseBody: err?.response?.data,
            })
          })
      }
    },
    [r2State.userId, saveChangesFlag.isOn]
  )

  const restorePersons = useCallback(() => {
    const undoActions = []

    selectedRows.forEach((id) => {
      const action = deletedPersonActions.find((dpa) => dpa.id === id)
      undoActions.push(
        UNDO({
          id: action.actionId,
          partitionKey: action.partitionKey,
        })
      )
    })
    saveSls(undoActions)
  }, [deletedPersonActions, saveSls, selectedRows])

  return (
    <>
      <Card backgroundColor={colors.red02}>
        <Expander
          open={deletedPersonsExpanded}
          setOpen={(newState) => {
            setDeletedPersonsExpanded(newState)
          }}
        >
          <ExpanderHeader>
            <H5 supplementary>{t('delete-person.table.deleted-names.header', 'Deleted Names')}</H5>
          </ExpanderHeader>
          <ExpanderContent>
            <ResponsiveCheckboxTable
              totalColumnCount={3}
              onSelectionChange={setSelectedRows}
              atSizeColumns={{
                default: {
                  labels: [t('delete-person.table.name.label', 'Name'), t('delete-person.table.date.label', 'Date')],
                },
              }}
              backgroundColor={colors.red02}
            >
              {deletedPersonActions.length > 0 &&
                deletedPersonActions.map((personAction) => (
                  <ResponsiveTableRow
                    key={personAction.id}
                    name={personAction.id}
                    column1={<div data-testid={`delete-${personAction.name}column1-deleter`}>{personAction.name}</div>}
                    column2={
                      <>
                        {getTime(personAction?.journalAttribution?.timestamp, i18n)}
                        <Separator size="xxs" />
                        {getUser(personAction?.journalAttribution?.userId)}
                      </>
                    }
                  />
                ))}
            </ResponsiveCheckboxTable>
            <Button
              emphasis="high"
              data-testid="restore-button"
              onClick={() => restorePersons()}
              disabled={selectedRows.length === 0}
            >
              {t('delete-person.table.restore-button', 'Restore')}
            </Button>
          </ExpanderContent>
        </Expander>
      </Card>
      <ErrorOverlay
        error={saveError}
        setError={setSaveError}
        title={t('save-error-overlay.heading', 'Unable to Save Changes')}
        message={t('save-error-overlay.content-generic', 'Your changes were not saved.  Please try again later.')}
      />
    </>
  )
}
